// Derives the first-run "getting started" state (docs/10 #2.5) from real data —
// no onboarding-specific flags on the server, just whether the org has done the
// thing yet. Each step's `done` is recomputed on every render from the same
// queries the destination pages already use, so it can never drift out of sync
// with what the user actually sees when they click through.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useScreens } from '../api/screens.ts';
import { useMedia } from '../api/media.ts';
import { usePlaylists } from '../api/playlists.ts';
import { useSchedules } from '../api/schedules.ts';
import { useOrg } from '../api/org.ts';
import {
  isChecklistDismissed,
  dismissChecklist as persistChecklistDismissed,
  isWelcomeDismissed,
  dismissWelcome as persistWelcomeDismissed,
} from './storage.ts';

/** An org stops being "new" after this many days — the checklist/banner never show past it. */
export const ONBOARDING_YOUNG_ORG_DAYS = 30;

export type OnboardingStepId = 'pair' | 'media' | 'playlist' | 'schedule';

export interface OnboardingStep {
  id: OnboardingStepId;
  title: string;
  description: string;
  done: boolean;
  ctaLabel: string;
}

export interface OnboardingState {
  /** True once org + screens + media + playlists + schedules have all loaded. */
  isLoading: boolean;
  /** False for the small number of API/data-loading failures; steps just read as incomplete. */
  isYoungOrg: boolean;
  steps: OnboardingStep[];
  completedCount: number;
  total: number;
  allDone: boolean;
  checklistDismissed: boolean;
  dismissChecklist: () => void;
  hasNoScreens: boolean;
  welcomeDismissed: boolean;
  dismissWelcome: () => void;
}

export function useOnboarding(): OnboardingState {
  const org = useOrg();
  const screens = useScreens();
  const media = useMedia();
  const playlists = usePlaylists();
  const schedules = useSchedules();

  const orgId = org.data?.id;
  const isLoading = org.isLoading || screens.isLoading || media.isLoading || playlists.isLoading || schedules.isLoading;

  // Dismissal has to live in React state, not be recomputed from storage on
  // every render — otherwise clicking "dismiss" writes to localStorage but
  // nothing tells React to re-render, and the card just sits there until some
  // unrelated state change happens to sweep through. Re-sync from storage
  // whenever the active org changes (e.g. switching orgs mid-session).
  const [checklistDismissed, setChecklistDismissed] = useState(() => (orgId ? isChecklistDismissed(orgId) : false));
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => (orgId ? isWelcomeDismissed(orgId) : false));

  useEffect(() => {
    setChecklistDismissed(orgId ? isChecklistDismissed(orgId) : false);
    setWelcomeDismissed(orgId ? isWelcomeDismissed(orgId) : false);
  }, [orgId]);

  const dismissChecklist = useCallback(() => {
    if (!orgId) return;
    persistChecklistDismissed(orgId);
    setChecklistDismissed(true);
  }, [orgId]);

  const dismissWelcome = useCallback(() => {
    if (!orgId) return;
    persistWelcomeDismissed(orgId);
    setWelcomeDismissed(true);
  }, [orgId]);

  const isYoungOrg = useMemo(() => {
    if (!org.data) return false;
    const ageMs = Date.now() - org.data.created_at;
    return ageMs < ONBOARDING_YOUNG_ORG_DAYS * 24 * 60 * 60 * 1000;
  }, [org.data]);

  const pairDone = (screens.data ?? []).some((s) => s.status !== 'never_paired');
  const mediaDone = (media.data ?? []).length > 0;
  const playlistDone = (playlists.data ?? []).length > 0;
  const scheduleDone = (schedules.data ?? []).length > 0;

  const steps: OnboardingStep[] = [
    {
      id: 'pair',
      title: 'Pair your first screen',
      description: 'Open the player on a TV or media stick and enter its 6-character code.',
      done: pairDone,
      ctaLabel: 'Pair a screen',
    },
    {
      id: 'media',
      title: 'Upload media or create a slide',
      description: 'Add an image, a video, or a web page for screens to show.',
      done: mediaDone,
      ctaLabel: 'Go to Media',
    },
    {
      id: 'playlist',
      title: 'Build a playlist',
      description: 'Put your media into an ordered loop.',
      done: playlistDone,
      ctaLabel: 'Go to Playlists',
    },
    {
      id: 'schedule',
      title: 'Schedule it',
      description: 'Assign the playlist to a screen, group, or the whole org.',
      done: scheduleDone,
      ctaLabel: 'Go to Schedules',
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;

  return {
    isLoading,
    isYoungOrg,
    steps,
    completedCount,
    total: steps.length,
    allDone: completedCount === steps.length,
    checklistDismissed,
    dismissChecklist,
    hasNoScreens: (screens.data ?? []).length === 0,
    welcomeDismissed,
    dismissWelcome,
  };
}
